import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../../../common/hooks/hooks";
import { setSearch, setCodeSearch } from "../../../productSlice";
import Icon from "../../../../../../common/components/Icon/Icon";

interface SearchFilterProps {
  idPrefix: string;
}

const SearchFilter: React.FC<SearchFilterProps> = ({ idPrefix }) => {
  const dispatch = useAppDispatch();
  const { t, ready: areTranslationsReady } = useTranslation();

  // Create a unique ID for the input field
  const searchInputId = `${idPrefix}product-search-input`;

  // Get the current search term from Redux to pre-fill the input
  const currentSearchTerm = useAppSelector(
    (state) => state.product.filters.search
  );
  const codeSearch = useAppSelector((state) => state.product.filters.codeSearch);

  // Local state for the input field to allow for debouncing
  const [localSearchTerm, setLocalSearchTerm] = useState(currentSearchTerm);

  // Update local state if the Redux state changes (e.g., from URL params or reset)
  useEffect(() => {
    setLocalSearchTerm(currentSearchTerm);
  }, [currentSearchTerm]);

  // Debounce effect to update Redux store after user stops typing
  useEffect(() => {
    const handler = setTimeout(() => {
      // Only dispatch if the local term is different from the Redux term
      if (localSearchTerm !== currentSearchTerm) {
        dispatch(setSearch(localSearchTerm));
      }
    }, 500); // 500ms debounce delay

    // Cleanup function to cancel the timeout if the component unmounts or the term changes
    return () => {
      clearTimeout(handler);
    };
  }, [localSearchTerm, currentSearchTerm, dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchTerm(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Immediately dispatch on submit (e.g., pressing Enter)
    if (localSearchTerm !== currentSearchTerm) {
      dispatch(setSearch(localSearchTerm));
    }
  };

  const isComponentLoading = !areTranslationsReady;

  return (
    <div className="filter-widget">
      {/* Title removed for a more compact, responsive layout */}

      <form className="filter-widget__search-form" onSubmit={handleSubmit}>
        <label htmlFor={searchInputId} className="sr-only">
          {t("productsArchive.sidebar.searchTitle")}
        </label>
        <input
          type="search"
          id={searchInputId}
          className="filter-widget__search-input"
          value={localSearchTerm}
          onChange={handleInputChange}
          placeholder={
            isComponentLoading
              ? ""
              : t(codeSearch ? "referenceCodes.searchPlaceholder" : "productsArchive.sidebar.searchPlaceholder")
          }
          aria-label={t("productsArchive.sidebar.searchTitle")}
        />
        <button
          type="submit"
          className="filter-widget__search-button"
          aria-label={t("productsArchive.sidebar.searchButtonLabel")}
        >
          <Icon iconName="search" className="icon--stroke-thick" />
        </button>
      </form>
      <label className="filter-widget__code-mode">
        <input
          type="checkbox"
          checked={codeSearch}
          onChange={(event) => dispatch(setCodeSearch(event.target.checked))}
        />
        {t("referenceCodes.onlyCodes")}
      </label>
    </div>
  );
};

export default SearchFilter;
